import { Bot, Check, ChevronRight, FileText, Loader2, User } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import type { ChatStreamState, ChatTurnItem, ChatTurnTraces } from "@/lib/api";
import { cn } from "@/lib/utils";

import { Markdown } from "./markdown";
import { SuggestionCard } from "./suggestion-card";
import type { ChatAttachment, ChatMessage, MachineSummary } from "./types";
import { isImageAttachment, machineDetails, machineOptions } from "./utils";

/**
 * Friendly labels for the retrieval steps the assistant runs mid-stream. Keys
 * match the backend tool names emitted in `tool_use` events; anything not
 * listed falls back to a generic "Working…" line.
 */
const TOOL_LABELS: Record<string, string> = {
  search_bom: "Searching BOM",
  search_catalog_names: "Checking catalog",
  read_full_bom: "Reading full BOM",
  resolve_machine: "Identifying machine",
  report_part_suggestions: "Finalizing matches",
};

function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? "Working";
}

interface AttachmentChipProps {
  attachment: ChatAttachment;
}

function AttachmentChip({ attachment }: AttachmentChipProps) {
  if (isImageAttachment(attachment.content_type, attachment.file_name)) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        className="border-border/60 block overflow-hidden rounded-md border"
      >
        <img
          src={attachment.url}
          alt={attachment.file_name}
          className="h-20 w-20 object-cover"
          loading="lazy"
        />
      </a>
    );
  }

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className="border-border/60 bg-background/70 text-foreground hover:bg-background flex max-w-[12rem] items-center gap-1.5 rounded-md border px-2 py-1 text-xs"
    >
      <FileText className="text-muted h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{attachment.file_name}</span>
    </a>
  );
}

interface MachineSelectionProps {
  machines: MachineSummary[];
  onSelect: (machine: MachineSummary) => void;
  disabled: boolean;
}

/**
 * Headlines lead with the machine type and plate number rather than
 * `model_name`, which reads 'A_DMM' on every DISA BOM machine — see
 * {@link machineOptions}.
 */
function MachineSelection({ machines, onSelect, disabled }: MachineSelectionProps) {
  const options = machineOptions(machines);
  return (
    <div className="border-border bg-accent-light/40 mt-3 space-y-2 rounded-xl border p-3">
      <p className="text-muted text-[11px] font-medium tracking-wide uppercase">
        Which machine do you mean?
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map(({ machine, label }) => (
          <Button
            key={machine.id}
            variant="outline"
            disabled={disabled}
            onClick={() => onSelect(machine)}
            className="h-auto flex-col items-start gap-0.5 py-2 text-left whitespace-normal"
          >
            <span className="text-foreground text-sm font-medium">{label}</span>
            <span className="text-muted text-xs">{machineDetails(machine, label)}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}

interface MessageBubbleProps {
  message: ChatMessage;
  conversationId: string;
  onSelectMachine: (machine: MachineSummary) => void;
  machineSelectDisabled: boolean;
  /**
   * Session-only chronological transcript for a finished assistant turn. When
   * present it drives the split between the collapsed "how this was found"
   * details and the turn's actual reply prose (see {@link splitTurn}), and the
   * plain `content` bubble is skipped so the reply isn't rendered twice. Absent
   * after a reload → falls back to the persisted `content` markdown.
   */
  trace?: ChatTurnItem[];
}

function MessageBubble({
  message,
  conversationId,
  onSelectMachine,
  machineSelectDisabled,
  trace,
}: MessageBubbleProps) {
  const isUser = message.role === "user";
  const hasSuggestions = !isUser && (message.suggestions?.length ?? 0) > 0;
  // Only assistant turns carry a trace; a user message never does.
  const turn = !isUser && trace !== undefined && trace.length > 0 ? splitTurn(trace) : null;
  // Prose falls back to the persisted `content` when there's no transcript to
  // split (a reload, or a user message) AND as a safety net when splitting
  // found no reply prose on a turn that produced no suggestion cards — a
  // card-less turn is a question, a "couldn't find it" or a caveat, so it must
  // never render as an empty bubble. When cards carry the answer, silent prose
  // is the intended outcome.
  const showPersistedContent = turn === null || (turn.reply.length === 0 && !hasSuggestions);

  return (
    <div className={cn("flex gap-2.5", isUser ? "flex-row-reverse" : "flex-row")}>
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          isUser ? "bg-primary text-primary-foreground" : "bg-accent-light text-primary",
        )}
      >
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>

      <div className={cn("max-w-[85%] min-w-0", isUser ? "items-end" : "items-start")}>
        <div className="space-y-2">
          {/* Retrieval steps + mid-loop narration, collapsed by default. */}
          {turn !== null && turn.details.length > 0 && (
            <TurnDetails entries={turn.details} live={false} />
          )}

          {turn?.reply.map((block) => (
            <div
              key={block.key}
              className="bg-card border-border text-foreground rounded-2xl rounded-tl-sm border px-3.5 py-2 text-sm break-words"
            >
              <Markdown text={block.text} />
            </div>
          ))}

          {showPersistedContent && message.content && (
            <div
              className={cn(
                "rounded-2xl px-3.5 py-2 text-sm break-words",
                isUser
                  ? "bg-primary text-primary-foreground rounded-tr-sm whitespace-pre-wrap"
                  : "bg-card border-border text-foreground rounded-tl-sm border",
              )}
            >
              {/* Assistant replies are GFM markdown; user input stays plain text. */}
              {isUser ? message.content : <Markdown text={message.content} />}
            </div>
          )}
        </div>

        {message.attachments.length > 0 && (
          <div className={cn("mt-2 flex flex-wrap gap-2", isUser && "justify-end")}>
            {message.attachments.map((attachment) => (
              <AttachmentChip key={attachment.id} attachment={attachment} />
            ))}
          </div>
        )}

        {!isUser && message.suggestions && message.suggestions.length > 0 && (
          <SuggestionCard
            suggestions={message.suggestions}
            conversationId={conversationId}
            messageId={message.id}
          />
        )}

        {!isUser &&
          message.needs_machine_selection &&
          message.needs_machine_selection.length > 0 && (
            <MachineSelection
              machines={message.needs_machine_selection}
              onSelect={onSelectMachine}
              disabled={machineSelectDisabled}
            />
          )}
      </div>
    </div>
  );
}

/**
 * One tool row in the transcript. A row can represent a single call or a
 * contiguous run of same-name calls (see {@link foldTranscript}); the status
 * is aggregate — spinner while ANY call is still running, check once ALL are
 * done, destructive if ANY completed call failed.
 */
interface ToolRowData {
  key: string;
  name: string;
  running: boolean;
  failed: boolean;
}

function ToolRow({ row }: { row: ToolRowData }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-1 text-xs",
        row.failed ? "text-destructive" : row.running ? "text-foreground" : "text-muted",
      )}
    >
      {row.running ? (
        <Loader2 className="text-primary h-3.5 w-3.5 shrink-0 animate-spin" />
      ) : (
        <Check
          className={cn("h-3.5 w-3.5 shrink-0", row.failed ? "text-destructive" : "text-primary")}
        />
      )}
      <span className="min-w-0 truncate">
        {toolLabel(row.name)}
        {row.running ? "…" : ""}
      </span>
      {row.failed && <span className="text-destructive shrink-0">· failed</span>}
    </div>
  );
}

type TranscriptEntry =
  | { kind: "text"; key: string; text: string }
  | ({ kind: "tool" } & ToolRowData);

/**
 * Fold the ordered item list into render entries, collapsing CONTIGUOUS runs of
 * same-name `tool` items into one aggregated row. Text items are a different
 * kind, so they naturally break a run — a burst of same-name tools between two
 * prose blocks becomes one row, while a later burst of the same tool after
 * other prose/tools is its own row (chronology preserved). No count is tracked:
 * the row shows only the friendly label. Aggregate status matches the old
 * grouping (spinner if any running, check once all done, failed if any
 * completed call has `ok:false`).
 */
function foldTranscript(items: ChatTurnItem[]): TranscriptEntry[] {
  const entries: TranscriptEntry[] = [];
  items.forEach((item, index) => {
    if (item.kind === "text") {
      entries.push({ kind: "text", key: `text-${index}`, text: item.text });
      return;
    }
    const running = item.status === "running";
    const failed = item.status === "done" && item.ok === false;
    const last = entries.at(-1);
    if (last && last.kind === "tool" && last.name === item.name) {
      last.running = last.running || running;
      last.failed = last.failed || failed;
    } else {
      entries.push({ kind: "tool", key: `tool-${item.id}`, name: item.name, running, failed });
    }
  });
  return entries;
}

interface TurnSplit {
  /**
   * Everything that belongs behind the details disclosure, in chronological
   * order: the tool rows plus the mid-loop narration.
   */
  details: TranscriptEntry[];
  /** The turn's reply prose, in order — usually zero or one block. */
  reply: { key: string; text: string }[];
}

/**
 * Split a turn into "how it was found" (hidden by default) and "the reply".
 *
 * The engine runs a multi-step loop, so the model can write prose between tool
 * calls. We treat a prose block as INTERMEDIATE NARRATION when a further tool
 * call follows it — the model wrote it on the way to an answer it didn't have
 * yet ("Let me check the BOM for that pump…"), which is exactly the
 * step-by-step chatter we're hiding. The reply is therefore the contiguous run
 * of prose after the LAST tool call.
 *
 * Chosen over "render only the final text block" because it never hides a
 * trailing block: if the model asks a clarifying question and then adds a
 * sentence, both survive. And a turn with no tool calls at all (a greeting, or
 * a question the model can ask without looking anything up) has `lastTool` of
 * -1, so every block is reply — that case can't be hidden by construction.
 */
function splitTurn(items: ChatTurnItem[]): TurnSplit {
  let lastTool = -1;
  items.forEach((item, index) => {
    if (item.kind === "tool") {
      lastTool = index;
    }
  });
  return {
    details: foldTranscript(items.slice(0, lastTool + 1)),
    // Nothing after the last tool call can be a tool item, so this is just the
    // narrowing `.slice()` can't express.
    reply: items
      .slice(lastTool + 1)
      .flatMap((item, index) =>
        item.kind === "text" ? [{ key: `reply-${index}`, text: item.text }] : [],
      ),
  };
}

interface TurnDetailsProps {
  entries: TranscriptEntry[];
  /**
   * True while the turn is still open — the trigger then doubles as the
   * "Thinking…" indicator instead of a summary of finished work.
   */
  live: boolean;
}

/**
 * The retrieval steps of a turn, collapsed behind a disclosure and closed by
 * default. Field technicians want the answer, not the search log, but the log
 * has to stay reachable for when a suggestion looks wrong — so it's one plain
 * line ("How this was found · 3 steps") that expands into the chronological
 * transcript: tool rows plus whatever the model narrated between them.
 *
 * While the turn is live this same control IS the loading indicator (spinner +
 * "Thinking…"), so a working turn shows exactly one line no matter how many
 * tools run. A plain `useState` disclosure rather than a Collapsible primitive:
 * it's a button plus a conditional render, and the repo has no Collapsible.
 */
function TurnDetails({ entries, live }: TurnDetailsProps) {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  const steps = entries.reduce((count, entry) => (entry.kind === "tool" ? count + 1 : count), 0);
  const anyFailed = entries.some((entry) => entry.kind === "tool" && entry.failed);

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={contentId}
        className="text-muted hover:text-foreground focus-visible:ring-ring flex max-w-full items-center gap-2 rounded-md px-1 py-0.5 text-xs transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        {live && <Loader2 className="text-primary h-3.5 w-3.5 shrink-0 animate-spin" />}
        <span className="truncate">{live ? "Thinking…" : "How this was found"}</span>
        {!live && steps > 0 && (
          <span className="shrink-0">{`· ${steps} ${steps === 1 ? "step" : "steps"}`}</span>
        )}
        {anyFailed && <span className="text-destructive shrink-0">· a step failed</span>}
        <ChevronRight
          className={cn("h-3.5 w-3.5 shrink-0 transition-transform", open && "rotate-90")}
        />
      </button>

      {open && (
        <div id={contentId} className="border-border/60 ml-2 space-y-1.5 border-l pl-2.5">
          {entries.map((entry) =>
            entry.kind === "text" ? (
              // Narration is inspection material here, not a reply — subdued
              // text rather than a chat bubble.
              <div key={entry.key} className="text-muted px-1 text-xs">
                <Markdown text={entry.text} />
              </div>
            ) : (
              <ToolRow key={entry.key} row={entry} />
            ),
          )}
        </div>
      )}
    </div>
  );
}

interface StreamingTurnProps {
  stream: ChatStreamState;
}

/**
 * The in-progress assistant turn while a stream is open.
 *
 * A working turn deliberately shows ONE line — the collapsed
 * {@link TurnDetails} control, spinning and labelled "Thinking…" — and no
 * prose: the answer belongs in the suggestion cards, and the model's mid-loop
 * commentary is noise a technician has to scroll past. Everything streamed so
 * far (tool rows and any prose, including the answer's partial tokens) sits
 * inside that disclosure for anyone who wants to watch it, and moves out into a
 * reply bubble once the turn lands (see {@link splitTurn}) — nothing is thrown
 * away, so a question the model streamed here is on screen a moment later.
 *
 * Before the first item arrives there's nothing to disclose, so the indicator
 * is a boxed bubble instead — an immediate acknowledgement of the send. On
 * error the disclosure stays (that's when the steps matter most) but stops
 * spinning, since `isStreaming` is already false by then.
 */
function StreamingTurn({ stream }: StreamingTurnProps) {
  const entries = foldTranscript(stream.items);

  return (
    <div className="flex gap-2.5">
      <div className="bg-accent-light text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
        <Bot className="h-4 w-4" />
      </div>
      <div className="flex max-w-[85%] min-w-0 flex-col gap-2">
        {entries.length > 0 ? (
          <TurnDetails entries={entries} live={stream.isStreaming} />
        ) : (
          stream.isStreaming &&
          !stream.error && (
            <div className="bg-card border-border text-muted flex items-center gap-2 rounded-2xl rounded-tl-sm border px-3.5 py-2 text-sm">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Thinking…
            </div>
          )
        )}

        {stream.error && (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-2xl rounded-tl-sm border px-3.5 py-2 text-sm">
            {stream.error}
          </div>
        )}
      </div>
    </div>
  );
}

interface MessageListProps {
  messages: ChatMessage[];
  conversationId: string;
  isThinking: boolean;
  streamState: ChatStreamState;
  /** Session-only interleaved transcripts keyed by assistant message id. */
  traces: ChatTurnTraces;
  onSelectMachine: (machine: MachineSummary) => void;
}

/** Scrollable list of chat messages that auto-scrolls to the latest content. */
export function MessageList({
  messages,
  conversationId,
  isThinking,
  streamState,
  traces,
  onSelectMachine,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // While a stream is open the user's turn arrives via the `meta` event and is
  // only merged into the persisted cache on `done` — so render it optimistically
  // here, but suppress it once the same id is already in `messages` to avoid a
  // duplicate during the merge/reset transition.
  const streamedUser = streamState.userMessage;
  const showStreamedUser = streamedUser !== null && !messages.some((m) => m.id === streamedUser.id);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isThinking, streamState.items]);

  return (
    <div className="space-y-5">
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          conversationId={conversationId}
          onSelectMachine={onSelectMachine}
          machineSelectDisabled={isThinking}
          trace={traces[message.id]}
        />
      ))}
      {showStreamedUser && (
        <MessageBubble
          message={streamedUser}
          conversationId={conversationId}
          onSelectMachine={onSelectMachine}
          machineSelectDisabled={isThinking}
        />
      )}
      {(streamState.isStreaming || streamState.error) && <StreamingTurn stream={streamState} />}
      <div ref={bottomRef} />
    </div>
  );
}
